import {
  Badge,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { MdAssignment, MdLock, MdInsights } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCheckMobileScreen } from "../../useCheckMobileScreen";

const features = [
  {
    icon: MdAssignment,
    title: "Structured",
    description:
      "A validated framework designed to produce consistent and reliable results across all participants.",
  },
  {
    icon: MdLock,
    title: "Confidential",
    description:
      "Your responses are fully secure. All data is handled with strict confidentiality.",
  },
  {
    icon: MdInsights,
    title: "Insightful",
    description:
      "Receive a detailed, personalised report with clear, actionable findings.",
  },
];

function CustomLandingPage() {
  const [code, setCode] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useCheckMobileScreen();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeParam = queryParams.get("code");
    if (codeParam) {
      setCode(codeParam);
      navigate("/custom/access-code", { state: { accessCode: codeParam, autoSubmit: true } });
    }
  }, [location, navigate]);

  return (
    <Flex
      direction="column"
      flexGrow={1}
      px={isMobile ? 4 : 8}
      pb={12}
    >
      {/* Hero section */}
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        py={isMobile ? 10 : 16}
        textAlign="center"
      >
        <Badge
          colorScheme="teal"
          variant="subtle"
          fontSize="xs"
          letterSpacing="wider"
          textTransform="uppercase"
          px={3}
          py={1}
          borderRadius="full"
          mb={5}
        >
          Assessment Platform
        </Badge>
        <Heading
          color="white"
          fontSize={isMobile ? "2xl" : "4xl"}
          fontWeight="700"
          mb={4}
          lineHeight="shorter"
        >
          Begin Your{" "}
          <Text as="span" color="teal.300">
            Assessment
          </Text>
        </Heading>
        <Text
          color="gray.400"
          fontSize={isMobile ? "sm" : "md"}
          maxW="460px"
          mb={8}
          lineHeight="tall"
        >
          A structured, evidence-based evaluation designed to support your
          professional growth and development. Enter your access code to get
          started.
        </Text>
        <Button
          leftIcon={<CheckIcon />}
          colorScheme="teal"
          size={isMobile ? "md" : "lg"}
          onClick={() =>
            navigate("/custom/access-code", { state: { accessCode: code } })
          }
          px={10}
        >
          Enter Access Code
        </Button>
      </Flex>

      <Divider borderColor="whiteAlpha.200" maxW="900px" alignSelf="center" w="100%" mb={10} />

      {/* Feature cards */}
      <SimpleGrid
        columns={isMobile ? 1 : 3}
        spacing={5}
        maxW="900px"
        alignSelf="center"
        w="100%"
      >
        {features.map((f) => (
          <Flex
            key={f.title}
            direction="column"
            bg="whiteAlpha.50"
            borderRadius="lg"
            border="1px solid"
            borderColor="whiteAlpha.100"
            p={6}
            alignItems="flex-start"
            _hover={{ bg: "whiteAlpha.100", borderColor: "teal.700" }}
            transition="all 0.2s"
          >
            <Icon as={f.icon} boxSize={7} color="teal.300" mb={3} />
            <Heading fontSize="md" color="white" mb={2}>
              {f.title}
            </Heading>
            <Text fontSize="sm" color="gray.400" lineHeight="tall">
              {f.description}
            </Text>
          </Flex>
        ))}
      </SimpleGrid>
    </Flex>
  );
}

export default CustomLandingPage;
